import { useBehaviorSubject } from '@euler/utils/hooks';
import { NavigationContainerRef } from '@react-navigation/native';
import { AnimatePresence, MotiView } from 'moti';
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Easing } from 'react-native-reanimated';
import { useSiteInspectionNavigatorContext } from './SiteInspectionContext';
import {
  SiteInspectionNavParams,
  SiteInspectionNavigator,
} from './SiteInspectionNavigator';
import { SiteInspectionFlowRef } from './types';

export const SiteInspectionSidePanelFlow = memo(
  forwardRef<
    SiteInspectionFlowRef,
    { onDismiss?: (result?: any) => void; onPresent?: () => void }
  >(({ onDismiss, onPresent }, ref) => {
    const [initialScreen, updateInitialScreen] = useState<{
      screen: keyof SiteInspectionNavParams;
      params?: any;
      key?: string;
    }>();

    const finishWithResult = useRef<any>();

    const navigatorRef =
      useRef<NavigationContainerRef<SiteInspectionNavParams>>(null);

    const { bottomComponent$, preventNavigateAway$, onWillDismiss$ } =
      useSiteInspectionNavigatorContext();

    const [bottomComponent] = useBehaviorSubject(bottomComponent$);

    const isDismissed = useRef(false);

    const onInternalDismiss = useCallback(() => {
      if (isDismissed.current) return;

      let isDefaultPrevented = false;

      const dismiss = () => {
        isDismissed.current = true;
        updateInitialScreen(undefined);
        onDismiss?.(finishWithResult.current);
        finishWithResult.current = undefined;
        preventNavigateAway$.next(false);
      };

      const event = {
        preventDefault: () => {
          isDefaultPrevented = true;
        },
        dismiss,
      };

      onWillDismiss$.next(event);

      if (isDefaultPrevented) {
        return;
      }

      dismiss();
    }, [onDismiss, onWillDismiss$, preventNavigateAway$]);

    useImperativeHandle(ref, () => {
      return {
        get navigator() {
          return navigatorRef.current!;
        },
        present<S extends keyof SiteInspectionNavParams>(
          screen: S,
          params: SiteInspectionNavParams[S],
          key?: string,
        ) {
          updateInitialScreen({ screen, params, key });
          onPresent?.();
        },
        finish(result?: any) {
          finishWithResult.current = result;
        },
      };
    });

    if (initialScreen == null) {
      return null;
    }

    isDismissed.current = false;

    return (
      <AnimatePresence exitBeforeEnter>
        <MotiView
          key={initialScreen.key}
          from={{ opacity: 0, scale: 0.75 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.75 }}
          transition={{
            type: 'timing',
            duration: 150,
            easing: Easing.inOut(Easing.linear),
          }}
          css={`
            flex: 1;
          `}
        >
          <SiteInspectionNavigator
            ref={navigatorRef}
            initialScreen={initialScreen.screen}
            initialParams={initialScreen.params}
            onDismiss={onInternalDismiss}
          />
          {bottomComponent}
        </MotiView>
      </AnimatePresence>
    );
  }),
);
